import { store, useAppSelector } from "@app/store";
import {
  Blocked,
  NotVerified,
  RefusalToActivate,
} from "@components/auth/login/not-verified";
import {
  AdminLayout,
  AuthLayout,
  AuthWithExitLayout,
  LeadLayout,
  MerchantLayout,
  TraderLayout,
} from "@components/layout";
import { AlertContainer } from "@features/alert";
import { useAuth } from "@features/auth";
import { AuthState } from "@features/auth/slice";
import { FlashMessagesProvider } from "@hooks/messages/context";
import { WebSocketProvider } from "@request/context";
import { lazy } from "react";
import { Provider } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  RouteObject,
  useRoutes,
} from "react-router-dom";
import "./App.scss";
import { LoginForm } from "./components/auth/login";
import { EnrollmentOtpForm } from "./components/auth/login/enrollment-otp-form";
import { RegisterForm } from "./components/auth/register";
import { RegisterSuccess } from "./components/auth/register/success";
import { AdminAppealsPage } from "./pages/admin/appeals";
import { AdminBanksPage } from "./pages/admin/banks";
import { AdminDealsPage } from "./pages/admin/deals";
import { AdminFinancePage } from "./pages/admin/finance";
import { AdminNotificationBot } from "./pages/admin/notification-bot";
import { AdminPayoutsPage } from "./pages/admin/payouts";
import { SelectedUserPage } from "./pages/admin/selectedUser";
import { UsersPage } from "./pages/admin/users";
import { AdminWithdrawalsPage } from "./pages/admin/withdrawals";
import { CashRegistersPage } from "./pages/merchant/cash-registers";
import { DealsPage } from "./pages/merchant/deals";
import { MerchantDisputesPage } from "./pages/merchant/disputes";
import { FinancePage } from "./pages/merchant/finance";
import { MainPage } from "./pages/merchant/main";
import { PayoutsPage } from "./pages/merchant/payouts";
import { ConclusionsPage } from "./pages/trader/conclusions";
import { DealsPage as TraderDealsPage } from "./pages/trader/deals";
import { DetailPage } from "./pages/trader/detail";
import { DetailEditPage } from "./pages/trader/detail-edit";
import { DetailsPage } from "./pages/trader/details";
import { DevicePage } from "./pages/trader/device";
import { StatusCheckerPage } from "./pages/trader/devices";
import { DisputesPage } from "./pages/trader/disputes";
import { FinancePage as TraderFinancePage } from "./pages/trader/finance";
import { MainPage as TraderMainPage } from "./pages/trader/main";
import { WithdrawalsPage } from "./pages/merchant/withdrawals";
import { LeadDealsPage } from "./pages/lead/deals";
import { PartnersPage } from "./pages/lead/partners";
import { LeadWithdrawalsPage } from "./pages/lead/withdrawals";
import { TraderWithdrawals } from "./pages/trader/withdrawals";
// import { API_URL_WS } from "@constants";
// import { useWebSocket } from "@request/useWebsocket";

const Requisites = lazy(() => import("./pages/requisites"));
const requisiteRoute = {
  path: "/requisites/:orderId",
  element: <Requisites />,
};

const routes = (state: AuthState): RouteObject[] => {
  const roles = state?.roles;
  const status = state?.status;

  if (!roles) {
    return [
      requisiteRoute,
      {
        path: "/",
        element: <AuthLayout />,
        children: [
          { index: true, element: <LoginForm withCode /> },
          { path: "first-auth", element: <LoginForm /> },
          { path: "register", element: <RegisterForm /> },
          { path: "register/success", element: <RegisterSuccess /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ];
  }

  if (roles.includes("need_verify_otp")) {
    return [
      requisiteRoute,
      {
        path: "/",
        element: <AuthWithExitLayout />,
        children: [
          { index: true, element: <EnrollmentOtpForm /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ];
  }

  if (status === "blocked") {
    return [
      requisiteRoute,
      {
        path: "/",
        element: <AuthWithExitLayout />,
        children: [
          { index: true, element: <Blocked /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ];
  }

  if (status === "rejected") {
    return [
      requisiteRoute,
      {
        path: "/",
        element: <AuthWithExitLayout />,
        children: [
          { index: true, element: <RefusalToActivate /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ];
  }

  if (roles.includes("not_verified") || status === "inactive") {
    return [
      requisiteRoute,
      {
        path: "/",
        element: <AuthWithExitLayout />,
        children: [
          { index: true, element: <NotVerified /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ];
  }

  const isSuperUser = roles.some((role) => role == "super_user");
  const isAdmin = roles.some((role) =>
    ["administrator", "super_user"].includes(role)
  );
  const isSupport = roles.some((role) => ["customer_support"].includes(role));
  const isTrader = roles.some((role) =>
    ["trader", "trader_teamlead"].includes(role)
  );
  const isTeamLead = roles.some((role) => role == "trader_teamlead");
  const isAgent = roles.some((role) => role == "merchant_agent");
  const isMerchant = roles.some((role) => ["merchant"].includes(role));

  if (
    !isAdmin &&
    !isSupport &&
    !isTrader &&
    !isMerchant &&
    !isTeamLead &&
    !isAgent
  ) {
    return [];
  }

  const leadLinkRole =
    isAdmin || isSupport || isTeamLead ? "teamlead" : isAgent ? "agent" : "";

  const defaultRole =
    isAdmin || isSupport
      ? "admin"
      : leadLinkRole.length > 0
        ? leadLinkRole
        : isTrader
          ? "trader"
          : "merchant";
  const defaultPath = `/${state.userId}/${defaultRole}`;
  const fullRoutes = [
    {
      path: "/",
      element: <Navigate to={defaultPath} replace />,
    },
    requisiteRoute,
    ...(isAdmin || isSupport
      ? [
          {
            path: "/:userId/admin",
            element: <AdminLayout />,
            children: [
              { index: true, path: "", element: <UsersPage /> },
              { path: "users/:selectedUserId", element: <SelectedUserPage /> },
              { path: "deals", element: <AdminDealsPage /> },
              { path: "settlements", element: <AdminPayoutsPage /> },
              { path: "appeals", element: <AdminAppealsPage /> },
              { path: "withdrawals", element: <AdminWithdrawalsPage /> },
              ...(isSuperUser
                ? [
                    { path: "finance", element: <AdminFinancePage /> },
                    { path: "banks", element: <AdminBanksPage /> },
                    {
                      path: "notfication-bot",
                      element: <AdminNotificationBot />,
                    },
                  ]
                : []),
              { path: "*", element: <Navigate to="/admin" replace /> },
            ],
          },
        ]
      : []),
    ...(isAdmin || isSupport || isTeamLead || isAgent
      ? [
          {
            path: `/:userId/teamlead`,
            element: <LeadLayout />,
            children: [
              { index: true, element: <LeadDealsPage /> },
              { path: "partners", element: <PartnersPage /> },
              { path: "withdrawals", element: <LeadWithdrawalsPage /> },
            ],
          },
        ]
      : []),
    ...(isAdmin || isSupport || isAgent
      ? [
          {
            path: `/:userId/agent`,
            element: <LeadLayout />,
            children: [
              { index: true, element: <LeadDealsPage /> },
              { path: "partners", element: <PartnersPage /> },
              { path: "withdrawals", element: <LeadWithdrawalsPage /> },
            ],
          },
        ]
      : []),
    ...(isAdmin || isSupport || isTrader
      ? [
          {
            path: "/:userId/trader",
            element: <TraderLayout />,
            children: [
              { index: true, element: <TraderMainPage /> },
              { path: "deals", element: <TraderDealsPage /> },
              { path: "details", element: <DetailsPage /> },
              { path: "details/:detailId", element: <DetailPage /> },
              { path: "details/:detailId/edit", element: <DetailEditPage /> },
              { path: "finance", element: <TraderFinancePage /> },
              { path: "finance/disputes", element: <DisputesPage /> },
              { path: "devices", element: <StatusCheckerPage /> },
              {
                path: "devices/:deviceId",
                element: <DevicePage />,
              },
              {
                path: "devices/:deviceId/edit",
                element: <DevicePage />,
              },
              { path: "payouts", element: <ConclusionsPage /> },
              { path: "payouts/withdrawals", element: <TraderWithdrawals /> },
            ],
          },
        ]
      : []),
    ...(isAdmin || isSupport || isMerchant
      ? [
          {
            path: "/:userId/merchant",
            element: <MerchantLayout />,
            children: [
              { index: true, element: <MainPage /> },
              { path: "deals", element: <DealsPage /> },
              { path: "deals/disputes", element: <MerchantDisputesPage /> },
              { path: "cash-registers", element: <CashRegistersPage /> },
              { path: "finance", element: <FinancePage /> },
              { path: "settlements", element: <PayoutsPage /> },
              { path: "withdrawals", element: <WithdrawalsPage /> },
            ],
          },
        ]
      : []),
    { path: "*", element: <Navigate to={defaultPath} replace /> },
  ];
  return fullRoutes;
};

const App = () => {
  return (
    <WebSocketProvider>
      <Provider store={store}>
        <FlashMessagesProvider closeTimeoutDelay={3500}>
          <AppContent />
        </FlashMessagesProvider>
      </Provider>
    </WebSocketProvider>
  );
};

function AppContent() {
  const status = useAppSelector((state) => state.auth.status);
  // useEffect(() => {
  //   let wsClient: WebSocketClient | null = null;
  //   if (state.accessToken != null) {
  //     // Initialize the WebSocket client with your URL and token
  //     wsClient = WebSocketClient.getInstance(
  //       API_URL_WS, // Replace with your WebSocket URL
  //       state.accessToken // Replace with your actual token
  //     );
  //     wsClient.connect();
  //     // Set event handlers
  //     // wsClient.setCallbacks(
  //     // (event) => console.log("Connected:", event),
  //     // (event) => {
  //     //   const data = JSON.parse(event.data);
  //     //   store.dispatch(addAlert(data.message));
  //     //   console.log(event.data);
  //     // },
  //     // (event) => console.error("Error:", event),
  //     // (event) => console.log("Closed:", event.code, event.reason)
  //     // );
  //   }
  //   // Cleanup: Close the connection when the app unmounts
  //   return () => {
  //     if (wsClient) wsClient.close();
  //   };
  // }, [state.accessToken]); // Empty dependency array ensures this runs once on mount
  // const { lastMessage } = useWebSocket(API_URL_WS, state.accessToken);

  // useEffect(() => {
  //   if (lastMessage) {
  //     console.log('Received message:', lastMessage.message);
  //     // Handle the message as needed
  //   }
  // }, [lastMessage]);
  return (
    <>
      <BrowserRouter>
        <Routes />
      </BrowserRouter>
      <AlertContainer />
      {status == "frozen" && <FrozenPin />}
    </>
  );
}

const FrozenPin = () => {
  return (
    <div
      style={{
        position: "absolute",
        top: "10px",
        left: "10px",
        width: "300px",
        border: "1px solid black",
        padding: "5px",
        background: "white",
      }}
    >
      Ваш аккаунт заморожен
    </div>
  );
};

const Routes = () => {
  const { state } = useAuth();

  return useRoutes(routes(state));
};

export default App;
